import React, { useState } from 'react';
import TextFieldWrapper from './TextFieldWrapper';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import TextField from '@material-ui/core/TextField/TextField';
import { errorTextStyles } from './styles';
import { RequestState } from '../utils/useRequestState';

export const shouldTextFieldFocus = (
    isFocus: true | undefined,
): true | undefined => {
    return window.innerWidth > 500 ? isFocus : undefined;
};

export const ErrorText: React.FC<{
    text: string | React.ReactChildren | React.ReactChild;
    className?: string;
}> = ({ text, className }) => {
    const { errorText } = errorTextStyles();
    return <div className={`${errorText} ${className}`}>{text}</div>;
};

export interface SignupTextFieldProps
    extends Partial<AutocompleteRenderInputParams> {
    name?: string;
    isOptional?: true;
    value: string | undefined;
    placeholder: string;
    type: 'email' | 'number' | 'tel' | 'text' | 'url';
    errorText?: string;
    nullErrorText?: string;
    className?: string;
    isFocus: true | undefined;
    onChange: (value: string, name?: string) => unknown;
    onSubmit?: () => unknown;
    onBlur?: () => unknown;
    onSkip?: () => unknown;
    validation: (value: string | undefined) => boolean;
    maxLength: number;
    requestState?: RequestState<{}>;
    onPressEnter?: () => unknown;
}

export const ENTER_KEY_CODE = 13;
export const UP_KEY_CODE = 38;
export const DOWN_KEY_CODE = 40;

const SignupTextField: React.FC<SignupTextFieldProps> = (props) => {
    const {
        name,
        value,
        placeholder,
        type,
        isOptional,
        isFocus,
        errorText,
        nullErrorText,
        className,
        maxLength,
        requestState,
        onBlur,
        onChange,
        onSubmit,
        onSkip,
        validation,
        onPressEnter,
        ...rest
    } = props;
    const [error, setError] = useState<boolean>(false);
    const onKeyPressFunc = (e: React.KeyboardEvent) => {
        if (e.which === ENTER_KEY_CODE) {
            if (onSubmit && validation(value)) {
                onSubmit();
            }
            if (onPressEnter) {
                onPressEnter();
            }
        }
    };

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;
        if (value.length > maxLength) return;
        if (error && validation(value)) {
            setError(false);
        }
        onChange(value, name);
    };

    const isValueValid = validation(value);
    const onBlurFunc = () => {
        if (isOptional && !value) return;
        onBlur && onBlur();
        return setError(!isValueValid);
    };

    const text =
        nullErrorText && value?.length === 0 ? nullErrorText : errorText;

    return (
        <TextFieldWrapper
            onSubmit={onSubmit}
            onSkip={onSkip}
            requestState={requestState}
            renderButton={isValueValid}
            value={isOptional && value}
            isOptional={isOptional}
            isError={error}
            className={className}
        >
            <TextField
                {...rest}
                autoFocus={shouldTextFieldFocus(isFocus)}
                type={type}
                placeholder={placeholder}
                name={name}
                value={value}
                onChange={onChangeInput}
                onKeyPress={onKeyPressFunc}
                onBlur={onBlurFunc}
                fullWidth={true}
            />
            {requestState?.type === 'REQUEST_ERROR' || (error && !!text) ? (
                <ErrorText
                    text={
                        requestState?.type === 'REQUEST_ERROR'
                            ? requestState.error.message
                            : text || ''
                    }
                />
            ) : null}
        </TextFieldWrapper>
    );
};

export default SignupTextField;
